import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../environments/environment';

export interface Proyecto {
  Id: string;
  UsuarioId: string;
  Nombre: string;
  Tipo: 'banner' | 'imagen_procesada';
  DatosProyecto: any;
  Miniatura?: string;
  FechaCreacion: string;
  FechaModificacion: string;
  Estado: string;
}

export interface ProyectoListItem {
  Id: string;
  Nombre: string;
  Tipo: string;
  Miniatura?: string;
  FechaCreacion: string;
  FechaModificacion: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProyectosService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Obtener todos los proyectos de un usuario
   */
  getProyectos(usuarioId: string): Observable<ProyectoListItem[]> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams().set('userId', usuarioId);

    return this.http.get<string>(`${this.apiUrl}/api/get-proyectos`, { 
      headers, 
      params,
      responseType: 'text' as 'json' // Recibir como texto para parsear manualmente
    }).pipe(
      map((response: any) => {
        // Si es string, parsearlo; si ya es objeto, devolverlo directamente
        if (typeof response === 'string') {
          try {
            return JSON.parse(response);
          } catch (e) {
            console.error('Error parseando respuesta de proyectos:', e);
            return [];
          }
        }
        return response;
      }),
      catchError(error => {
        console.error('Error obteniendo proyectos:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtener un proyecto por ID
   */
  getProyecto(proyectoId: string): Observable<Proyecto> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams().set('id', proyectoId);

    return this.http.get<string>(`${this.apiUrl}/api/get-proyecto`, { 
      headers, 
      params,
      responseType: 'text' as 'json'
    }).pipe(
      map((response: any) => {
        if (typeof response === 'string') {
          try {
            return JSON.parse(response);
          } catch (e) {
            console.error('Error parseando respuesta de proyecto:', e);
            throw new Error('Respuesta inválida del servidor');
          }
        }
        return response;
      }),
      catchError(error => {
        console.error('Error obteniendo proyecto:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crear un nuevo proyecto
   */
  createProyecto(proyecto: {
    usuarioId: string;
    nombre: string;
    tipo: 'banner' | 'imagen_procesada';
    datosProyecto: any;
    miniatura?: string;
  }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<any>(`${this.apiUrl}/api/create-proyecto`, proyecto, { 
      headers
    }).pipe(
      catchError(error => {
        console.error('Error creando proyecto:', error);
        console.error('Error completo:', JSON.stringify(error, null, 2));
        
        // Si el error tiene un body string, intentar parsearlo
        if (error.error && typeof error.error === 'string') {
          try {
            error.error = JSON.parse(error.error);
          } catch (e) {
            console.error('No se pudo parsear el error:', e);
          }
        } else if (error.error && error.error.text) {
          try {
            error.error = JSON.parse(error.error.text);
          } catch (e) {
            console.error('No se pudo parsear el error.text:', e);
          }
        }
        
        return throwError(() => error);
      })
    );
  }

  /**
   * Actualizar un proyecto
   */
  updateProyecto(proyectoId: string, updates: {
    nombre?: string;
    datosProyecto?: any;
    miniatura?: string;
  }): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = { proyectoId, ...updates };

    return this.http.put<string>(`${this.apiUrl}/api/update-proyecto`, body, { 
      headers,
      responseType: 'text' as 'json'
    }).pipe(
      map((response: any) => {
        // Parsear el string JSON
        if (typeof response === 'string') {
          try {
            return JSON.parse(response);
          } catch (e) {
            console.error('Error parseando respuesta de update-proyecto:', e);
            return { error: 'Respuesta inválida del servidor' };
          }
        }
        return response;
      }),
      catchError(error => {
        console.error('Error actualizando proyecto:', error);
        
        // Si el error tiene un body string, intentar parsearlo
        if (error.error && typeof error.error === 'string') {
          try {
            error.error = JSON.parse(error.error);
          } catch (e) {
            console.error('No se pudo parsear el error:', e);
          }
        }
        
        return throwError(() => error);
      })
    );
  }

  /**
   * Eliminar un proyecto (soft delete)
   */
  deleteProyecto(proyectoId: string): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = new HttpParams().set('id', proyectoId);

    return this.http.delete<any>(`${this.apiUrl}/api/delete-proyecto`, { headers, params }).pipe(
      catchError(error => {
        console.error('Error eliminando proyecto:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Subir imagen a Azure Storage
   */
  uploadImage(imageFile: File): Observable<{ url: string; blobName: string }> {
    const formData = new FormData();
    formData.append('image', imageFile);

    return this.http.post<{ url: string; blobName: string }>(`${this.apiUrl}/api/upload-image`, formData).pipe(
      catchError(error => {
        console.error('Error subiendo imagen:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Subir imagen del procesador a Azure Storage (con nombre original)
   */
  uploadProcessorImage(imageFile: File): Observable<{ url: string; blobName: string; originalFileName: string }> {
    const formData = new FormData();
    formData.append('image', imageFile);

    return this.http.post<{ url: string; blobName: string; originalFileName: string }>(
      `${this.apiUrl}/api/upload-processor-image`, 
      formData
    ).pipe(
      catchError(error => {
        console.error('Error subiendo imagen del procesador:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtener imágenes del usuario
   */
  getImagenes(usuarioId: string): Observable<{ imagenes: Array<{ id: number; UsuarioId: string; url_image: string }>; total: number }> {
    return this.http.get<{ imagenes: Array<{ id: number; UsuarioId: string; url_image: string }>; total: number }>(
      `${this.apiUrl}/api/get-imagenes?usuarioId=${encodeURIComponent(usuarioId)}`
    ).pipe(
      catchError(error => {
        console.error('Error obteniendo imágenes:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Guardar registro de imagen en la base de datos
   */
  saveImagen(usuarioId: string, url_image: string): Observable<{ id: number; usuarioId: string; url_image: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<{ id: number; usuarioId: string; url_image: string }>(
      `${this.apiUrl}/api/save-imagen`,
      { usuarioId, url_image },
      { headers }
    ).pipe(
      catchError(error => {
        console.error('Error guardando imagen en BD:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * FUNCIONALIDAD DE ELIMINAR FONDO TEMPORALMENTE DESHABILITADA
   * Eliminar fondo de imagen usando el endpoint RemoveBackground
   */
  // removeBackground(imageFile: File, model: string = 'birefnet-massive'): Observable<{ original_url: string; processed_url: string; processing_type: string }> {
  //   const formData = new FormData();
  //   formData.append('file', imageFile);
    
  //   const params = new HttpParams().set('model', model);

  //   return this.http.post<{ original_url: string; processed_url: string; processing_type: string }>(
  //     `${this.apiUrl}/api/RemoveBackground`,
  //     formData,
  //     { params }
  //   ).pipe(
  //     catchError(error => {
  //       console.error('Error eliminando fondo:', error);
  //       return throwError(() => error);
  //     })
  //   );
  // }
}



